// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useState } from 'react';

import { getApiBaseUrl } from '@/services/apiBaseUrl';

/**
 * A golf course the signed-in user is allowed to manage (edit). Returned by
 * GET /api/geodata/courses/manageable — an admin gets all official/pending courses; any user gets the
 * courses they created (regardless of visibility).
 */
export type ManageableCourse = {
  id: string;
  name: string;
  clubName: string;
  city: string;
  visibility: string;
  /** The club's website, or null when no source we trust has stated one. */
  clubWebsiteUrl: string | null;
};

type ManageableCourseResponse = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
  clubName?: string;
  ClubName?: string;
  city?: string;
  City?: string;
  visibility?: string;
  Visibility?: string;
  clubWebsiteUrl?: string | null;
  ClubWebsiteUrl?: string | null;
};

const mapManageableCourse = (row: ManageableCourseResponse): ManageableCourse | null => {
  const id = (row.id ?? row.Id ?? '').trim();
  const name = (row.name ?? row.Name ?? '').trim();
  if (!id || !name) {
    return null;
  }
  return {
    id,
    name,
    clubName: (row.clubName ?? row.ClubName ?? '').trim(),
    city: (row.city ?? row.City ?? '').trim(),
    visibility: (row.visibility ?? row.Visibility ?? 'official').trim().toLowerCase(),
    // Empty string and absent both mean "no website" — normalised to null here so the form has one
    // falsy value to test rather than two.
    clubWebsiteUrl: (row.clubWebsiteUrl ?? row.ClubWebsiteUrl ?? '').trim() || null,
  };
};

export type ManageableCoursesState = {
  courses: ManageableCourse[];
  loading: boolean;
  /** Re-fetch the manageable list (e.g. after a course was created/edited). */
  refresh: () => void;
};

/**
 * Loads the courses the signed-in user may manage. Returns an empty list until a token is available.
 * Used both to gate the "Golf courses" menu item and to populate the manage-course selector.
 */
export function useManageableCourses(token: string | null | undefined): ManageableCoursesState {
  const [courses, setCourses] = useState<ManageableCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!token) {
      setCourses([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const apiBaseUrl = getApiBaseUrl();
    setLoading(true);

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/courses/manageable`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok || cancelled) {
          if (!cancelled) {
            setCourses([]);
          }
          return;
        }

        const payload = (await response.json()) as ManageableCourseResponse[];
        if (cancelled || !Array.isArray(payload)) {
          return;
        }

        setCourses(payload.map(mapManageableCourse).filter((c): c is ManageableCourse => c !== null));
      } catch {
        if (!cancelled) {
          setCourses([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  return { courses, loading, refresh };
}
